/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ComponentRef,
  ViewContainerRef,
  Type,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil, catchError, of } from 'rxjs';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { SMEService } from '../../services/sme.service';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

// Import all form components
import { BusinessLoanFormComponent } from '../../../search/components/business-loan-form/business-loan-form.component';
import { TechnicalAssistanceFormComponent } from '../../../search/components/technical-assistance-form/technical-assistance-form.component';
import { OperationalToolsFormComponent } from '../../../search/components/operational-tools-form/operational-tools-form.component';
import { RealEstateFormComponent } from '../../../search/components/real-estate-form/real-estate-form.component';
import { SolarEnergyFormComponent } from '../../../search/components/solar-energy-form/solar-energy-form.component';
import { FindingInvestorsFormComponent } from '../../../search/components/finding-investors-form/finding-investors-form.component';

interface ProductConfig {
  title: string;
  component: Type<any>;
  propertyMapFn: (properties: any[]) => any;
  reverseMapFn: (formData: any) => any;
  extractInputs?: (data: any) => any;
}

@Component({
  selector: 'app-product-detail-page',
  templateUrl: './product-detail-page.component.html',
  styleUrls: ['./product-detail-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    BusinessLoanFormComponent,
    TechnicalAssistanceFormComponent,
    OperationalToolsFormComponent,
    RealEstateFormComponent,
    SolarEnergyFormComponent,
    FindingInvestorsFormComponent,
  ],
})
export class ProductDetailPageComponent
  extends MenuComponentBase
  implements OnInit
{
  @ViewChild('formContainer', { read: ViewContainerRef })
  formContainer!: ViewContainerRef;
  @ViewChild('dynamicForm') dynamicForm: any;

  private smeService = inject(SMEService);
  private location = inject(Location);
  private router = inject(Router);
  override route = inject(ActivatedRoute);
  private commonService = inject(CommonService);

  isLoading = false;
  isSaving = false;
  initialFormData: any = null;
  additionalInputs: any = {};
  pageTitle = '';
  pageDescription = '';
  productType = '';
  rawProperties: any[] = [];

  private productConfig!: ProductConfig;
  private formComponentRef?: ComponentRef<any>;

  private productConfigs: Record<string, ProductConfig> = {
    'loans-capital': {
      title: 'Loans and Capital Products',
      component: BusinessLoanFormComponent,
      propertyMapFn: this.mapLoansCapitalData.bind(this),
      reverseMapFn: this.reverseMapLoansCapitalData.bind(this),
      extractInputs: (data: any) => ({
        creditScoreRange: data.creditScoreRange,
      }),
    },
    'technical-assistance': {
      title: 'Small Business Technical Assistance',
      component: TechnicalAssistanceFormComponent,
      propertyMapFn: this.mapTechnicalAssistanceData.bind(this),
      reverseMapFn: this.reverseMapTechnicalAssistanceData.bind(this),
    },
    'operational-tools': {
      title: 'Business Operational Tools',
      component: OperationalToolsFormComponent,
      propertyMapFn: this.mapOperationalToolsData.bind(this),
      reverseMapFn: this.reverseMapOperationalToolsData.bind(this),
    },
    'real-estate': {
      title: 'Commercial Real Estate',
      component: RealEstateFormComponent,
      propertyMapFn: this.mapRealEstateData.bind(this),
      reverseMapFn: this.reverseMapRealEstateData.bind(this),
    },
    'solar-energy': {
      title: 'Solar and Energy Efficiency Products',
      component: SolarEnergyFormComponent,
      propertyMapFn: this.mapSolarEnergyData.bind(this),
      reverseMapFn: this.reverseMapSolarEnergyData.bind(this),
      extractInputs: (data: any) => ({
        creditScoreRange: data.creditScoreRange,
      }),
    },
    'finding-investors': {
      title: 'Finding Investors',
      component: FindingInvestorsFormComponent,
      propertyMapFn: this.mapFindingInvestorsData.bind(this),
      reverseMapFn: this.reverseMapFindingInvestorsData.bind(this),
    },
  };

  private productDescriptions: Record<string, string> = {
    'loans-capital':
      'Update your loan requirements, financial information, and capital product preferences to help organizations match you with the right funding opportunities.',
    'technical-assistance':
      'Share your business needs and areas where you require support to receive targeted technical assistance and business development resources.',
    'operational-tools':
      'Indicate which operational tools and services would benefit your business to connect with relevant service providers and resources.',
    'real-estate':
      'Provide details about your commercial real estate needs to help identify suitable properties and facility upgrade opportunities.',
    'solar-energy':
      'Share your interest in solar energy and energy efficiency upgrades to connect with providers and financing options.',
    'finding-investors':
      'Describe your investor preferences and business needs to attract potential investors and funding partners.',
  };

  override ngOnInit(): void {
    super.ngOnInit();
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.productType = params['type'];
      this.productConfig = this.productConfigs[this.productType];

      if (!this.productConfig) {
        this.router.navigate(['/business']);
        return;
      }

      this.pageTitle = this.productConfig.title;
      this.pageDescription = this.productDescriptions[this.productType] || '';
      this.loadBusinessProfile();
    });
  }

  loadBusinessProfile(): void {
    const stateProfile = history.state?.businessProfile;
    if (stateProfile && stateProfile.detail?.properties) {
      this.isLoading = false;
      this.processBusinessProfile(stateProfile);
    } else {
      this.isLoading = true;
      this.smeService
        .getSMEBusinessRecord()
        .pipe(
          takeUntil(this.destroy$),
          catchError(() => {
            this.isLoading = false;
            this.commonService.triggerToast({
              message: 'Failed to load business profile',
              type: ToastType.Error,
            });
            return of(null);
          }),
        )
        .subscribe({
          next: (profile) => {
            this.isLoading = false;
            if (profile) {
              this.processBusinessProfile(profile);
            }
          },
        });
    }
  }

  processBusinessProfile(profile: any): void {
    if (profile?.detail?.properties) {
      this.rawProperties = profile.detail.properties;
      const mappedData = this.productConfig.propertyMapFn(
        profile.detail.properties,
      );
      this.initialFormData = mappedData;

      if (this.productConfig.extractInputs) {
        this.additionalInputs = this.productConfig.extractInputs(mappedData);
      }
    }
  }

  onFormSubmit(formData: any): void {
    this.isSaving = true;
    const mappedPayload = this.productConfig.reverseMapFn(formData);

    const properties = Object.entries(mappedPayload).map(([key, val]) => ({
      property: key,
      value: val,
    }));

    const payload = { properties };

    this.smeService
      .updateSMEBusinessRecord(payload)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.isSaving = false;
          this.commonService.triggerToast({
            message: 'Failed to update business profile',
            type: ToastType.Error,
          });
          return of(null);
        }),
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.isSaving = false;
            this.commonService.triggerToast({
              message: 'Business profile updated successfully',
              type: ToastType.Success,
            });
            this.router.navigate(['/business']);
          }
        },
      });
  }

  goBack(): void {
    this.location.back();
  }

  onSubmit(): void {
    if (this.dynamicForm) {
      this.dynamicForm.onSubmitForm();
    }
  }

  // Mapping functions for each product type
  private mapLoansCapitalData(properties: any[]): any {
    const data: any = {};
    const propertyMap: Record<string, string> = {
      has_registered_entity: 'stateRegisteredEntity',
      capital_product_type: 'loanType',
      will_fill_prescreening: 'willingToFillPrescreeningForm',
      loan_amount: 'requestingLoanAmount',
      loan_term_months: 'loanTermMonths',
      contributing_personal_capital: 'contributingPersonalCapital',
      personal_capital_percentage: 'personalCapitalPercentage',
      reporting_period: 'reportingPeriod',
      period_months: 'monthsInPeriod',
      total_revenue: 'totalRevenue',
      net_profit: 'netProfit',
      depreciation_amortization: 'depreciation',
      total_assets: 'totalAssets',
      cash_equivalents: 'cashEquivalents',
      receivables: 'tradeReceivables',
      inventory_value: 'inventoryValue',
      current_liabilities: 'totalCurrentLiabilities',
      shareholder_equity: 'shareholderEquity',
      alternative_net_worth: 'alternativeNetWorth',
      existing_loans_amount: 'outstandingLoans',
      credit_lines_limit: 'approvedCreditLimit',
      monthly_debt_service: 'monthlyDebtService',
      total_personal_income: 'personalIncome',
      personal_net_worth: 'personalNetWorth',
      personal_loans_amount: 'personalLoans',
      credit_score_range: 'creditScoreRange',
      has_co_signer: 'haveCoSigner',
      has_business_plan: 'haveBusinessPlan',
      has_financial_projections: 'haveFinancialProjections',
      project_description: 'loanUse',
      collateral_types: 'collateralTypes',
      financial_documentation: 'financialDocumentation',
      ownership_structure: 'owner_ship_structure',
      owner_ship_structure: 'owner_ship_structure',
    };

    const booleanFields = [
      'has_registered_entity',
      'will_fill_prescreening',
      'contributing_personal_capital',
      'has_co_signer',
      'has_financial_projections',
    ];

    properties.forEach((prop: any) => {
      const formFieldName = propertyMap[prop.property];
      if (formFieldName && prop.value !== null && prop.value !== undefined) {
        const val = this.getMappedPropertyValue(prop);
        if (booleanFields.includes(prop.property)) {
          data[formFieldName] = this.toBool(val);
        } else {
          data[formFieldName] = val;
        }
      }
    });

    return data;
  }

  private reverseMapLoansCapitalData(formData: any): any {
    const payload: any = {};
    const reverseMap: Record<string, string> = {
      stateRegisteredEntity: 'has_registered_entity',
      loanType: 'capital_product_type',
      willingToFillPrescreeningForm: 'will_fill_prescreening',
      requestingLoanAmount: 'loan_amount',
      loanTermMonths: 'loan_term_months',
      contributingPersonalCapital: 'contributing_personal_capital',
      personalCapitalPercentage: 'personal_capital_percentage',
      reportingPeriod: 'reporting_period',
      monthsInPeriod: 'period_months',
      totalRevenue: 'total_revenue',
      netProfit: 'net_profit',
      depreciation: 'depreciation_amortization',
      totalAssets: 'total_assets',
      cashEquivalents: 'cash_equivalents',
      tradeReceivables: 'receivables',
      inventoryValue: 'inventory_value',
      totalCurrentLiabilities: 'current_liabilities',
      shareholderEquity: 'shareholder_equity',
      alternativeNetWorth: 'alternative_net_worth',
      outstandingLoans: 'existing_loans_amount',
      approvedCreditLimit: 'credit_lines_limit',
      monthlyDebtService: 'monthly_debt_service',
      personalIncome: 'total_personal_income',
      personalNetWorth: 'personal_net_worth',
      personalLoans: 'personal_loans_amount',
      creditScoreRange: 'credit_score_range',
      haveCoSigner: 'has_co_signer',
      haveBusinessPlan: 'has_business_plan',
      haveFinancialProjections: 'has_financial_projections',
      loanUse: 'project_description',
      collateralTypes: 'collateral_types',
      financialDocumentation: 'financial_documentation',
      owner_ship_structure: 'ownership_structure',
    };

    Object.entries(formData).forEach(([key, value]) => {
      const apiFieldName = reverseMap[key];
      if (apiFieldName) {
        if (value === null || value === undefined || value === '') {
          payload[apiFieldName] = null;
        } else if (key === 'owner_ship_structure' && Array.isArray(value)) {
          payload[apiFieldName] = value.map((owner: any) => ({
            owner_name: owner.ownerName,
            ownership_percentage: owner.ownershipPercentage,
            other_business_percentage: owner.otherBusinessesPercentage,
          }));
        } else {
          payload[apiFieldName] = this.getPayloadValue(apiFieldName, value);
        }
      }
    });

    return payload;
  }

  private mapTechnicalAssistanceData(properties: any[]): any {
    const data: any = {};
    const propertyMap: Record<string, string> = {
      business_industry: 'industry',
      business_help_aspects: 'selectedSupportAreas',
      interested_in_selling_business: 'interestedInSellingBusiness',
      interested_in_employee_strategies: 'interestedInEmployeeStrategies',
      interested_in_succession_planning: 'interestedInSuccessionPlanning',
      worked_with_support_org: 'workedWithSupportOrg',
      willing_to_pay_for_assistance: 'willingToPay',
    };

    const booleanFields = [
      'interested_in_succession_planning',
      'worked_with_support_org',
    ];

    properties.forEach((prop: any) => {
      const formFieldName = propertyMap[prop.property];
      if (formFieldName && prop.value !== null && prop.value !== undefined) {
        const val = this.getMappedPropertyValue(prop);
        if (booleanFields.includes(prop.property)) {
          data[formFieldName] = this.toBool(val);
        } else {
          data[formFieldName] = val;
        }
      }
    });

    return data;
  }

  private reverseMapTechnicalAssistanceData(formData: any): any {
    const payload: any = {};
    const reverseMap: Record<string, string> = {
      industry: 'business_industry',
      selectedSupportAreas: 'business_help_aspects',
      interestedInSellingBusiness: 'interested_in_selling_business',
      interestedInEmployeeStrategies: 'interested_in_employee_strategies',
      interestedInSuccessionPlanning: 'interested_in_succession_planning',
      workedWithSupportOrg: 'worked_with_support_org',
      willingToPay: 'willing_to_pay_for_assistance',
    };

    Object.entries(formData).forEach(([key, value]) => {
      const apiFieldName = reverseMap[key];
      if (apiFieldName) {
        if (value === null || value === undefined || value === '') {
          payload[apiFieldName] = null;
        } else {
          payload[apiFieldName] = this.getPayloadValue(apiFieldName, value);
        }
      }
    });

    return payload;
  }

  private mapOperationalToolsData(properties: any[]): any {
    const data: any = { selectedTools: {} };
    const propertyMap: Record<string, string> = {
      business_banking: 'businessBanking',
      marketing_help: 'marketingHelp',
      tech_support: 'techSupport',
      business_insurance: 'businessInsurance',
      new_technology: 'newTechnology',
    };

    properties.forEach((prop: any) => {
      const formFieldName = propertyMap[prop.property];
      if (formFieldName && prop.value !== null && prop.value !== undefined) {
        const val = this.getMappedPropertyValue(prop);
        data.selectedTools[formFieldName] = val;
        data[formFieldName] = val;
      }
    });

    return data;
  }

  private reverseMapOperationalToolsData(formData: any): any {
    const payload: any = {};
    const reverseMap: Record<string, string> = {
      businessBanking: 'business_banking',
      marketingHelp: 'marketing_help',
      techSupport: 'tech_support',
      businessInsurance: 'business_insurance',
      newTechnology: 'new_technology',
    };

    const tools = formData.selectedTools || formData;

    Object.entries(tools).forEach(([key, value]) => {
      const apiFieldName = reverseMap[key];
      if (apiFieldName) {
        if (value === null || value === undefined || value === '') {
          payload[apiFieldName] = null;
        } else {
          payload[apiFieldName] = this.getPayloadValue(apiFieldName, value);
        }
      }
    });

    return payload;
  }

  private mapRealEstateData(properties: any[]): any {
    const data: any = {};
    const propertyMap: Record<string, string> = {
      looking_to_rent_or_buy: 'lookingToRentOrBuy',
      looking_to_upgrade_facility: 'lookingToUpgrade',
      own_facility: 'ownFacility',
      building_owner_willing_to_support: 'buildingOwnerWillingToSupport',
      interested_in_energy_upgrades: 'interestedInEnergyUpgrades',
    };

    const booleanFields = [
      'looking_to_upgrade_facility',
      'own_facility',
      'interested_in_energy_upgrades',
    ];

    properties.forEach((prop: any) => {
      const formFieldName = propertyMap[prop.property];
      if (formFieldName && prop.value !== null && prop.value !== undefined) {
        const val = this.getMappedPropertyValue(prop);
        if (booleanFields.includes(prop.property)) {
          data[formFieldName] = this.toBool(val);
        } else {
          data[formFieldName] = val;
        }
      }
    });

    return data;
  }

  private reverseMapRealEstateData(formData: any): any {
    const payload: any = {};
    const reverseMap: Record<string, string> = {
      lookingToRentOrBuy: 'looking_to_rent_or_buy',
      lookingToUpgrade: 'looking_to_upgrade_facility',
      ownFacility: 'own_facility',
      buildingOwnerWillingToSupport: 'building_owner_willing_to_support',
      interestedInEnergyUpgrades: 'interested_in_energy_upgrades',
    };

    Object.entries(formData).forEach(([key, value]) => {
      const apiFieldName = reverseMap[key];
      if (apiFieldName) {
        if (value === null || value === undefined || value === '') {
          payload[apiFieldName] = null;
        } else {
          payload[apiFieldName] = this.getPayloadValue(apiFieldName, value);
        }
      }
    });

    return payload;
  }

  private mapSolarEnergyData(properties: any[]): any {
    const data: any = {};
    const propertyMap: Record<string, string> = {
      interested_in_solar_provider_or_loan: 'interestedInSolar',
      interested_in_e_e_upgrades: 'interestedInEnergyEfficiency',
      building_built_year: 'buildingAge',
      building_improvements: 'buildingImprovements',
      power_utility_providers: 'powerProviders',
      interested_in_batteries: 'interestedInBatteries',
      can_provide_power_use_statement: 'canProvidePowerStatement',
      has_electric_vehicles: 'hasElectricVehicles',
      looking_to_add_electric_vehicle: 'interestedInElectricVehicle',
      credit_score_range: 'creditScoreRange',
    };

    const booleanFields = [
      'interested_in_solar_provider_or_loan',
      'interested_in_e_e_upgrades',
      'can_provide_power_use_statement',
      'has_electric_vehicles',
      'looking_to_add_electric_vehicle',
    ];

    properties.forEach((prop: any) => {
      const formFieldName = propertyMap[prop.property];
      if (prop.value !== null && prop.value !== undefined) {
        const val = this.getMappedPropertyValue(prop);
        if (booleanFields.includes(prop.property)) {
          data[formFieldName] = this.toBool(val);
        } else {
          data[formFieldName] = val;
        }
      }
    });

    return data;
  }

  private reverseMapSolarEnergyData(formData: any): any {
    const payload: any = {};
    const reverseMap: Record<string, string> = {
      interestedInSolar: 'interested_in_solar_provider_or_loan',
      interestedInEnergyEfficiency: 'interested_in_e_e_upgrades',
      buildingAge: 'building_built_year',
      buildingImprovements: 'building_improvements',
      powerProviders: 'power_utility_providers',
      interestedInBatteries: 'interested_in_batteries',
      canProvidePowerStatement: 'can_provide_power_use_statement',
      hasElectricVehicles: 'has_electric_vehicles',
      interestedInElectricVehicle: 'looking_to_add_electric_vehicle',
      creditScoreRange: 'credit_score_range',
    };

    Object.entries(formData).forEach(([key, value]) => {
      const apiFieldName = reverseMap[key];
      if (apiFieldName) {
        if (value === null || value === undefined || value === '') {
          payload[apiFieldName] = null;
        } else {
          payload[apiFieldName] = this.getPayloadValue(apiFieldName, value);
        }
      }
    });

    return payload;
  }

  private mapFindingInvestorsData(properties: any[]): any {
    const data: any = {};
    const propertyMap: Record<string, string> = {
      knows_investor_type: 'knowInvestorType',
      investment_types: 'selectedInvestmentTypes',
      willing_to_relocate: 'willingToRelocate',
    };

    properties.forEach((prop: any) => {
      const formFieldName = propertyMap[prop.property];
      if (formFieldName && prop.value !== null && prop.value !== undefined) {
        const val = this.getMappedPropertyValue(prop);
        data[formFieldName] = val;
      }
    });

    return data;
  }

  private reverseMapFindingInvestorsData(formData: any): any {
    const payload: any = {};
    const reverseMap: Record<string, string> = {
      knowInvestorType: 'knows_investor_type',
      selectedInvestmentTypes: 'investment_types',
      willingToRelocate: 'willing_to_relocate',
    };

    Object.entries(formData).forEach(([key, value]) => {
      const apiFieldName = reverseMap[key];
      if (apiFieldName) {
        if (value === null || value === undefined || value === '') {
          payload[apiFieldName] = null;
        } else {
          payload[apiFieldName] = this.getPayloadValue(apiFieldName, value);
        }
      }
    });

    return payload;
  }

  private toBool(val: any): boolean | null {
    if (val === null || val === undefined || val === '') return null;
    const str = String(val).toLowerCase();
    if (str === 'yes' || str === 'true') return true;
    if (str === 'no' || str === 'false') return false;
    return null;
  }

  private fromBool(val: any): string | null {
    if (val === true) return 'yes';
    if (val === false) return 'no';
    return val;
  }

  private getPayloadValue(propertyName: string, formValue: any): any {
    const prop = this.rawProperties?.find((p) => p.property === propertyName);
    if (!prop) {
      return formValue;
    }

    if (typeof formValue === 'boolean') {
      if (prop.options) {
        const optionValues = Object.values(prop.options);
        const hasBooleanOptionValues = optionValues.some(
          (v) => typeof v === 'boolean',
        );
        if (hasBooleanOptionValues) {
          return formValue;
        }
        if (formValue === true) {
          return prop.options['Yes'] !== undefined
            ? prop.options['Yes']
            : 'yes';
        } else {
          return prop.options['No'] !== undefined ? prop.options['No'] : 'no';
        }
      }
      return formValue ? 'yes' : 'no';
    }

    return formValue;
  }

  private getMappedPropertyValue(prop: any): any {
    if (prop.value === null || prop.value === undefined) {
      return prop.value;
    }
    if (prop.options) {
      if (Array.isArray(prop.value)) {
        return prop.value.map((val: any) => {
          return prop.options[val] !== undefined ? prop.options[val] : val;
        });
      } else {
        return prop.options[prop.value] !== undefined
          ? prop.options[prop.value]
          : prop.value;
      }
    }
    return prop.value;
  }

  getFormComponent() {
    return this.productConfig?.component;
  }
}
